import { useEffect, useState } from "react";

import dayjs from "dayjs";

import { cancelBatch, fetchBatchDetail, fetchBatchList } from "@/apis/reservation";
import Button from "@/components/Button";
import ConfirmModal from "@/components/Modal/ConfirmModal";
import ReservationModal from "@/components/Modal/ReservationModal";
import useModal from "@/hooks/useModal";
import type { BatchDetailResponse, BatchGroup } from "@/types/batchReservation";
import { DAY_OF_WEEK_LABEL, REPEAT_TYPE_LABEL } from "@/types/batchReservation";

const formatDate = (yyyymmdd: string) =>
  dayjs(yyyymmdd, "YYYYMMDD").format("YYYY.MM.DD");

const formatTime = (hhmmss: string | null) => {
  if (!hhmmss) return "-";
  return hhmmss.slice(0, 2) + ":" + hhmmss.slice(2, 4);
};

const getRepeatLabel = (group: BatchGroup): string => {
  const base = REPEAT_TYPE_LABEL[group.repeatType];
  if (group.repeatType === "weekly" && group.repeatDays) {
    const days = group.repeatDays
      .split(",")
      .map(d => DAY_OF_WEEK_LABEL[d.trim()] ?? d)
      .join(",");
    return `${base} (${days})`;
  }
  if (group.repeatType === "monthly" && group.repeatDays) {
    const days = group.repeatDays
      .split(",")
      .map(d => `${d.trim()}일`)
      .join(",");
    return `${base} (${days})`;
  }
  return base;
};

const STATUS_LABEL: Record<string, string> = {
  request: "승인 요청",
  approve: "승인 완료",
  reject: "승인 반려",
  cancel: "예약 취소",
};

const STATUS_BADGE_CLASS: Record<string, string> = {
  request: "bg-yellow-100 text-yellow-700",
  approve: "bg-green-100 text-green-700",
  reject: "bg-red-100 text-red-700",
  cancel: "bg-gray-100 text-gray-500",
};

const DAY_OF_WEEK_KO = ["일", "월", "화", "수", "목", "금", "토"];

const BatchGroupList = () => {
  const [batchList, setBatchList] = useState<BatchGroup[]>([]);
  const [selectedDetail, setSelectedDetail] = useState<BatchDetailResponse | null>(null);
  const [cancelTargetId, setCancelTargetId] = useState<number | null>(null);

  const detailModal = useModal();
  const confirmModal = useModal();

  const loadList = async () => {
    const data = await fetchBatchList();
    setBatchList(data);
  };

  useEffect(() => {
    loadList();
  }, []);

  const handleRowClick = async (batchId: number) => {
    const detail = await fetchBatchDetail(batchId);
    if (detail) {
      setSelectedDetail(detail);
      detailModal.onOpen();
    }
  };

  const handleCancelClick = (e: React.MouseEvent, batchId: number) => {
    e.stopPropagation();
    setCancelTargetId(batchId);
    confirmModal.onOpen();
  };

  const [actionLoading, setActionLoading] = useState(false);

  const handleConfirmCancel = async () => {
    if (cancelTargetId === null || actionLoading) return;
    setActionLoading(true);
    try {
      await cancelBatch(cancelTargetId);
    } catch {
      // error handled in api
    } finally {
      setActionLoading(false);
    }
    confirmModal.onClose();
    setCancelTargetId(null);
    await loadList();
    if (selectedDetail?.batchId === cancelTargetId) {
      detailModal.onClose();
      setSelectedDetail(null);
    }
  };

  const colClass = "px-3 py-2 text-left";
  const headerClass = `${colClass} text-small font-semibold text-gray-500 bg-gray-50`;

  return (
    <div className="flex flex-col gap-4">
      {batchList.length === 0 ? (
        <div className="flex h-20 items-center justify-center text-gray-500">
          등록된 일괄 예약 그룹이 없습니다.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-sm border border-gray-dull bg-white">
          <table className="w-full min-w-max text-base">
            <thead>
              <tr className="border-b border-gray-dull">
                <th className={headerClass}>그룹명</th>
                <th className={headerClass}>반복유형</th>
                <th className={headerClass}>기간</th>
                <th className={headerClass}>장소</th>
                <th className={headerClass}>시간</th>
                <th className={headerClass}>등록/건너뜀</th>
                <th className={headerClass}>생성일</th>
                <th className={headerClass}></th>
              </tr>
            </thead>
            <tbody>
              {batchList.map(group => (
                <tr
                  key={group.batchId}
                  className="cursor-pointer border-b border-gray-dull last:border-0 hover:bg-gray-50"
                  onClick={() => handleRowClick(group.batchId)}
                >
                  <td className={colClass}>{group.groupName}</td>
                  <td className={colClass}>{getRepeatLabel(group)}</td>
                  <td className={`${colClass} whitespace-nowrap`}>
                    {formatDate(group.startDate)} ~ {formatDate(group.endDate)}
                  </td>
                  <td className={colClass}>{group.plcCd ?? "-"}</td>
                  <td className={`${colClass} whitespace-nowrap`}>
                    {formatTime(group.startTime)} ~ {formatTime(group.endTime)}
                  </td>
                  <td className={colClass}>
                    {group.createdCount}/{group.skippedCount}
                  </td>
                  <td className={colClass}>{formatDate(group.crDt)}</td>
                  <td className={colClass}>
                    <Button
                      variant="outlined"
                      className="text-small border-red-300 text-red-500 hover:bg-red-50"
                      onClick={e => handleCancelClick(e, group.batchId)}
                    >
                      전체 취소
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detailModal.isOpen && selectedDetail && (
        <ReservationModal onClose={detailModal.onClose}>
          <ReservationModal.Header>
            <div className="text-xl font-light">{selectedDetail.groupName}</div>
            <div className="text-small font-light opacity-80">
              {getRepeatLabel(selectedDetail)} &middot;{" "}
              {formatDate(selectedDetail.startDate)} ~ {formatDate(selectedDetail.endDate)}
            </div>
          </ReservationModal.Header>

          <ReservationModal.Content>
            <div className="flex flex-col gap-4">
              {/* 기본 정보 */}
              <div className="flex flex-col gap-1 rounded-sm border border-gray-dull p-3 text-base">
                <div className="flex gap-2">
                  <span className="w-16 text-small text-gray-500">장소</span>
                  <span>{selectedDetail.plcCd ?? "-"}</span>
                </div>
                <div className="flex gap-2">
                  <span className="w-16 text-small text-gray-500">시간</span>
                  <span>
                    {formatTime(selectedDetail.startTime)} ~{" "}
                    {formatTime(selectedDetail.endTime)}
                  </span>
                </div>
                <div className="flex gap-2">
                  <span className="w-16 text-small text-gray-500">사용목적</span>
                  <span>{selectedDetail.useCnts ?? "-"}</span>
                </div>
                <div className="flex gap-2">
                  <span className="w-16 text-small text-gray-500">등록/건너뜀</span>
                  <span>
                    {selectedDetail.createdCount}건 등록 / {selectedDetail.skippedCount}건 건너뜀
                  </span>
                </div>
              </div>

              {/* 예약 목록 */}
              <div>
                <p className="mb-2 text-small font-semibold text-gray-600">예약 목록</p>
                {selectedDetail.reservations.length === 0 ? (
                  <div className="flex h-12 items-center justify-center text-small text-gray-400">
                    예약 내역이 없습니다.
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-sm border border-gray-dull">
                    <table className="w-full text-base">
                      <thead>
                        <tr className="border-b border-gray-dull bg-gray-50">
                          <th className="px-3 py-2 text-left text-small font-semibold text-gray-500">날짜</th>
                          <th className="px-3 py-2 text-left text-small font-semibold text-gray-500">시간</th>
                          <th className="px-3 py-2 text-left text-small font-semibold text-gray-500">상태</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedDetail.reservations.map(rsvt => {
                          const date = dayjs(rsvt.rsvtDt, "YYYYMMDD");
                          const dayOfWeek = DAY_OF_WEEK_KO[date.day()];
                          const badgeClass =
                            STATUS_BADGE_CLASS[rsvt.sttCd] ?? STATUS_BADGE_CLASS["cancel"];
                          return (
                            <tr
                              key={rsvt.rsvtId}
                              className="border-b border-gray-dull last:border-0"
                            >
                              <td className="whitespace-nowrap px-3 py-2 text-small">
                                {date.format("YYYY.MM.DD")} {dayOfWeek}요일
                              </td>
                              <td className="whitespace-nowrap px-3 py-2 text-small">
                                {formatTime(rsvt.startTime)} ~ {formatTime(rsvt.endTime)}
                              </td>
                              <td className="px-3 py-2">
                                <span
                                  className={`rounded-sm px-2 py-0.5 text-small ${badgeClass}`}
                                >
                                  {STATUS_LABEL[rsvt.sttCd] ?? rsvt.sttCd}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* 건너뛴 날짜 */}
              {selectedDetail.skippedCount > 0 && (
                <div>
                  <p className="mb-2 text-small font-semibold text-gray-600">건너뛴 날짜</p>
                  <div className="flex items-center justify-center rounded-sm border border-gray-dull p-3 text-small text-gray-500">
                    {selectedDetail.skippedCount}건의 날짜가 건너뛰어졌습니다.
                  </div>
                </div>
              )}
            </div>
          </ReservationModal.Content>

          <ReservationModal.Footer>
            <Button
              variant="outlined"
              className="border-red-300 text-red-500 hover:bg-red-50"
              onClick={e => handleCancelClick(e, selectedDetail.batchId)}
            >
              전체 취소
            </Button>
            <Button variant="primary" onClick={detailModal.onClose}>
              닫기
            </Button>
          </ReservationModal.Footer>
        </ReservationModal>
      )}

      {confirmModal.isOpen && (
        <ConfirmModal
          title="일괄 예약 취소"
          onConfirm={handleConfirmCancel}
          onClose={confirmModal.onClose}
        >
          해당 그룹의 모든 예약을 취소하시겠습니까?
        </ConfirmModal>
      )}
    </div>
  );
};

export default BatchGroupList;
